// @flow

import React, { useState, useEffect, useReducer, useRef, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { localize } from "redux-i18n";
import camelcase from "camelcase";
//$FlowIgnore
import DataGrid, { useRowSelection } from "react-data-grid";
import { GSRenderers } from "./renderers";
import { cloneObject } from "@utils/object";

import { ContextMenu, MenuItem, Checkbox, View, GSTooltip } from "@components";
import type { ContextMenuConfigType, MenuItemType } from "@components";

import type {
  RenderCellProps,
  RenderHeaderCellProps,
  CellClickArgs,
  CellMouseEvent,
  SortColumn,
  RowsChangeData,
} from "react-data-grid";

const colMinWidth = 50;
const colMaxWidth = "100vw";
// let timer: TimeoutID | null = null;

export type GSGAlignmentTypes = string | "left" | "center" | "right";
export type GSGridColumnTypes = "id" | "date" | "dateTime" | "status" | "action";

export type Props = {
  id: string,
  rows: Object[],
  columns: GSGridColumn[],
  /** columnsOrder?: string[]
   * It will accept string array and will try to show
   * only the columns in that array
   * (intended to work with the ColumnOrganiser)
   */
  columnsOrder?: string[],
  className?: string,
  noResultsText?: string,
  groupColumn?: GSGridGroupColumn, // Rename that prop to a better naming
  enableVirtualization?: ?boolean,
  onRowsChange?: ?(rows: $ReadOnlyArray<Object>, data: RowsChangeData<Object>) => void,
  selectedRows?: Set<number>,
  // In order to have selectable rows with checkboxes just add this function
  onSelectedRowsChange: (selectedRows: Set<number>) => Set<number> | null,
  rowKeyGetter?: Function,
  onCellClick?: (args: CellClickArgs<Object>, event: CellMouseEvent) => void,
  onCellContextMenu?: (args: CellClickArgs<Object>, event: CellMouseEvent) => void,
  onCellDoubleClick?: (args: CellClickArgs<Object>, event: CellMouseEvent) => void,
  onColumnResize?: (idx: number, width: number) => void,
  onColumnsReorder?: (sourceColumnKey: string, targetColumnKey: string) => void,
  rowClass?: (row: Object, rowIdx: number) => ?string,
  onSortColumnsChange?: Function,
  t: Function,
  rowHeight?: number | ((row: Object) => number),
  sortColumns?: Array<SortColumn>,
  headerRowHeight?: number | Function,
  rowHeight?: number,
  height?: number | string,
  maxHeight?: number | string,
};

export type GSGridColumn = {
  key: string,
  name?: string,
  tooltip?: {
    title?: string, // If no title is provided the tooltip's title will be the column name
    text: string,
  },
  cellTooltip?: {
    title?: string,
    text: string,
  },
  align?: GSGAlignmentTypes,
  resizable?: boolean,
  typeConfig?: GSGridColumnTypes, // Predefined templates (width, align, frozen etc.) for different column types
  className?: string,
  headerCellClass?: string,
  cellClass?: string,
  frozen?: boolean | "left" | "right",
  hidden?: boolean,
  contextMenuConfig?: Function => ContextMenuConfigType,
  editorOptions?: {
    displayCellContent?: boolean,
    commitOnOutsideClick?: boolean,
  },
  onCellClick?: (row: Object) => void,
  renderCell?: (
    props: RenderCellProps<Object>,
  ) => React$Element<React$ElementType> | number | string,
  renderEditCell?: (props: RenderCellProps<Object>) => React$Element<React$ElementType>,
  renderHeaderCell?: (props: RenderHeaderCellProps<Object>) => React$Element<React$ElementType>,
  sortable?: boolean,
  maxWidth?: string | number,
  minWidth?: string | number,
  width?:
    | number
    | string
    | "max-content" // The intrinsic preferred width.
    | "min-content" // The intrinsic minimum width.
    | "fit-content" // Use the available space, but not more than max-content, i.e min(max-content, max(min-content, stretch)).
    | "fit-content(T)" // Use the available space, but not more than max-content, i.e min(max-content, max(min-content, stretch)).
    | "auto"
    | "inherit"
    | "initial"
    | "revert"
    | "revert-layer"
    | "unset",
};

export type ContextMenuShowType = {
  top: number,
  left: number,
  config: ContextMenuConfigType,
};
export type GSGridGroupColumn = {
  ...GSGridColumn,
  isExpanded: boolean,
  hierarchy: [],
};

export type SelectedCellType = {
  start: { row: number, col: number },
  end: { row: number, col: number },
};

export type Row = { [key: string]: * };
function GSGrid(props: Props) {
  const gridRef = useRef(null);
  // const gridRef = (useRef < HTMLDivElement) | (null > null);
  const columnsOrder = props.columnsOrder;
  const propCols = props.columns;

  //Context menu state
  const [tooltip, setTooltip]: [
    { title?: string, text: string, position: { x: number, y: number } } | null,
    Function,
  ] = useState(null);
  const [showContextMenu, setShowContextMenu]: [ContextMenuShowType, Function] = useState(false);
  const [intervalTimer, setIntervalTimer] = useState(null); // its called data instead of rows, since rows is widely used here and can conflict
  const [rows, setRows] = useState([]); // its called data instead of rows, since rows is widely used here and can conflict
  const [selectedCells, setSelectedCells]: [SelectedCellType, Function] = useState(null); // writing own functionality since the integrated support only 1 selected celll
  const [selectedRows, setSelectedRows]: [Set<number>, Function] = useState(new Set([]));
  const [expandedRowsIds, setExpandedRowsIds] = useState([]);

  const [hiddenColumns, setHiddenColumns]: [Array<string>, Function] = useState(
    propCols && propCols.filter(col => col?.hidden === true).map(col => col?.key),
  ); // array of the columns keys

  const [visibleColumns, setVisibleColumns]: [Array<GSGridColumn>, Function] = useState([]); // Columns that should be displayed to the user

  const enableVirtualization = props.hasOwnProperty("enableVirtualization")
    ? props.enableVirtualization
    : rows.length > 100;

  useEffect(() => {
    if (props.hasOwnProperty("selectedRows")) {
      setSelectedRows(new Set(props.selectedRows));
    }
  }, [props.selectedRows]);

  useLayoutEffect(() => {
    const rdgCell = gridRef.current && gridRef.current.element;
    if (rdgCell) {
      rdgCell.addEventListener("contextmenu", handleShowHeaderContextMenu);
      rdgCell.addEventListener("mousedown", handleMouseDown);
      rdgCell.addEventListener("mouseover", handleMouseOver);
      rdgCell.addEventListener("mouseout", handleMouseOut);
      rdgCell.addEventListener("mouseup", handleMouseUp);
      rdgCell.addEventListener("keydown", handleKeyDown);
      // rdgCell.addEventListener("click", handleClick);

      return () => {
        rdgCell.removeEventListener("contextmenu", handleShowHeaderContextMenu);
        rdgCell.removeEventListener("mousedown", handleMouseDown);
        rdgCell.removeEventListener("mouseover", handleMouseOver);
        rdgCell.removeEventListener("mouseout", handleMouseOut);
        rdgCell.removeEventListener("mouseup", handleMouseUp);
        rdgCell.removeEventListener("keydown", handleKeyDown);
        // rdgCell.removeEventListener("click", handleClick);
      };
    }
  }, [gridRef.current, rows]);

  useLayoutEffect(() => {
    if (props.groupColumn) {
      setRows(generateHierarchy({ items: props.rows }));
      // Doing that in order to preserve the expanded state if the rows are the same (in reporting section after changing the filters)
      const sameRows = rows && rows.every(r => props.rows.findIndex(pr => pr.id == r.id) > -1);
      setExpandedRowsIds(
        props.rows
          .filter(r => r.isExpanded || (sameRows && expandedRowsIds.includes(r.id)))
          .map(r => r.id),
      );
    } else {
      setRows(props.rows);
    }

    if (props.onSelectedRowsChange && selectedRows.size) {
      // Rows were updated so we check if some of the selected are missing
      // and remove them from the selection
      setSelectedRows(
        new Set(
          Array.from(selectedRows).filter((selRowId: number) => {
            const res = props.rows.map(row => row.id).includes(selRowId);
            return res;
          }),
        ),
      );
    }
  }, [props.rows]);

  useEffect(() => {
    if (!props.groupColumn) return;
    setRows(prevRows => {
      return generateHierarchy({ items: props.rows });
    });
  }, [expandedRowsIds]);

  useEffect(() => {
    getSelectedCellsArray() && highlightSelectedCells(getSelectedCellsArray());
    if (selectedCells === null) removeSelectionClasses();
  }, [selectedCells]);

  useEffect(() => {
    setVisibleColumns(_ => {
      const alignedColumns = alignRightFrozenCols(propCols);
      return processGSGridColumns(alignedColumns);
    });
  }, [propCols, hiddenColumns]);

  useEffect(() => {
    columnsOrder &&
      setHiddenColumns(_ =>
        propCols
          .filter(c => !columnsOrder.includes(c.key) && c.frozen !== "right")
          .map(col => col.key),
      );
  }, [columnsOrder]);

  function rowKeyGetter(row: Row) {
    if (!row.id && !row.key && !row[propCols[0]?.key]) {
      console.log(
        `GSGrid warning\nEach child in a list should have a unique 'key' prop but there is no 'key' OR 'id' prop provided.\nPlease provide 'rowKeyGetter' function in order to tell React, which is the unique key of each row \nFalling back to JSON.stringify(row)\n${JSON.stringify(
          row,
        )}`,
      );

      return JSON.stringify(row);
    }
    const key = row.id || row.key || row[propCols[0]?.key];
    return key.toString();
  }

  function toggleExpand(clickedRow: Object, rows) {
    return function(e: MouseEvent) {
      if (!clickedRow || clickedRow.hasChildren === false) return;
      let expandedRowsIdsClone = [];
      setExpandedRowsIds((prevExpandedRowsIds: [number]) => {
        if (clickedRow.isExpanded) {
          // OFF
          return prevExpandedRowsIds.filter(expr => {
            return expr !== clickedRow.id;
          });
        }
        return [...prevExpandedRowsIds, clickedRow.id];
      });
    };
  }

  function generateHierarchy({
    items = [],
    parentId,
    level = 0,
    ancessors = [],
  }: {
    items: Object[],
    parentId?: number,
    level?: number,
    ancessors?: number[],
  }) {
    if (!items || !items.length) return [];
    return (
      items
        .filter(
          (
            item,
            idx, // Parents on the top
          ) =>
            (!parentId && !props.rows.some(i => i.id === item.parentId)) ||
            item.parentId === parentId,
        ) // Then alphabetically
        .sort((a, b) => {
          //Ordering the rows alphabetically
          if (a && b && a.name && b.name) return a.name.localeCompare(b.name);
          else return JSON.stringify(a).localeCompare(JSON.stringify(b));
        })

        //$FlowIgnore // TODO: we need to upgrade flow
        .flatMap(row => {
          const anc = parentId ? [...ancessors, parentId] : [];
          let res = [
            {
              ...row,
              level,
              isExpanded: row.expanded || expandedRowsIds.includes(row.id),
              ancessors: anc,
              hasChildren: props.rows.some(itm => row.id === itm.parentId),
            },
          ];
          if (row.id) {
            res.push(
              ...generateHierarchy({
                items,
                parentId: row.id,
                level: level + 1,
                ancessors: anc,
              }),
            );
          }

          return res;
        })
        //Hiding all children whose ancessors are not expanded
        .filter(row => row.ancessors.every(anc => expandedRowsIds.includes(anc))) // maybe here is a good place to reset expandedRowsIds so after expanding again to not remember what childtren were already expanded
        .filter(r => {
          return expandedRowsIds.includes(r && r.parentId) || (r && r.level === 0);
        })
    );
  }
  function columnConfigByType(column: GSGridColumn) {
    // We can manually add type so that type column pros will be applied to this column without need writing the manually
    if (!column.typeConfig || typeof column.typeConfig != "string") return column;

    // Applying default values to known type of columns so they look the same
    const columnTypes = {
      id: { width: 60 },
      date: { width: 90 },
      dateTime: { width: 120 },
      status: { width: 105 },
      action: { width: 50 },
    };
    return { ...columnTypes[column.typeConfig], ...column };
  }

  function processGSGridColumns(cols: Array<GSGridColumn>): Array<GSGridColumn> {
    let columns = [];

    // Reorder columns in case of columnsOrder
    if (columnsOrder)
      cols = [
        //$FlowIgnore
        ...columnsOrder.map(colKey => cols.find(col => col.key == colKey)),
        ...cols.filter(col => !columnsOrder.includes(col.key)),
      ];

    // Checkboxes on the
    if (
      props.hasOwnProperty("onSelectedRowsChange") &&
      typeof props.onSelectedRowsChange === "function"
    ) {
      columns.push({
        key: "selectRow",
        name: "",
        width: 35,
        minWidth: 35,
        maxWidth: 35,
        resizable: false,
        sortable: false,
        frozen: true,
        headerCellClass: "p-0 d-flex justify-content-center",
        cellClass: "p-0 d-flex justify-content-center",
        renderHeaderCell(props) {
          const [isRowSelected, onRowSelectionChange] = useRowSelection();
          return (
            <Checkbox
              aria-label="Select All"
              name={`gs-grid-row-`}
              tabIndex={props.tabIndex}
              value={isRowSelected}
              checked={isRowSelected}
              onChange={(isChecked: boolean) => {
                onRowSelectionChange({ type: "HEADER", checked: isChecked });
              }}
            />
          );
        },
        renderCell(props) {
          const [isRowSelected, onRowSelectionChange] = useRowSelection();
          return (
            <Checkbox
              aria-label="Select"
              name={`gs-grid-row-${props.row.id}`}
              tabIndex={props.tabIndex}
              value={isRowSelected}
              checked={isRowSelected}
              onChange={(isChecked, isShiftClick) => {
                onRowSelectionChange({
                  type: "ROW",
                  row: props.row,
                  checked: isChecked,
                  isShiftClick,
                });
              }}
            />
          );
        },
      });
    }

    // Hierarchy tree grid
    if (props.hasOwnProperty("groupColumn")) {
      columns.push({
        ...props.groupColumn,
        renderCell(params: Object) {
          return (
            <GSRenderers.cellExpandRenderer
              onCellExpand={toggleExpand(params.row, rows)}
              row={params.row}
            />
          );
        },
      });
    }

    // Adding some default props and checking for missing essential such
    const resCols = cols
      .filter(col => !hiddenColumns.includes(col.key))
      .map((column: GSGridColumn, colIdx: number) => {
        // Making sure there are the 2 essential props - key and name (that will be removed once we fix Flow or add TypeScript)

        if (!column.key && column.name) column.key = camelcase(column.name);
        if (!column.key && !column.name) {
          console.error(
            "You must provide key and/or name to every column of the grid and this is missing:",
            column,
          );
        }

        // Some types are automatically assigned
        if (column.key === "id" || column.name === "ID") column.typeConfig = "id";
        if (column.key === "status" || column.name === "Status") column.typeConfig = "status";
        if (column.key === "actionMenu") column.typeConfig = "action";
        // Some types are automatically assigned END

        // Using the type config
        if (column.typeConfig !== undefined && typeof column.typeConfig == "string") {
          column = { ...column, ...columnConfigByType(column) };
        }
        // Using the type config END

        // If we provide contextMenuConfig we automatically get last column as ContextMenu with the provided config
        if (column.contextMenuConfig && column.contextMenuConfig.length > 0) {
          column.renderCell = GSGrid.render.contextMenuTrigger;
          column.key = "actionsMenu";
          column.align = "center";
          column.renderHeaderCell = () => GSRenderers.materialIcon("menu");
        }
        // If we provide contextMenuConfig we automatically get last column as ContextMenu with the provided config END

        // if (!column.hasOwnProperty("width")) column.width = "min-content";
        if (typeof column.width === "number") column.width = `${column.width}px`; // In case of lazy provided value...

        // We can provide just the name of the renderer and GSGrid will try to do the rest
        if (typeof column.renderCell === "string") {
          column.renderCell = GSRenderers[column.renderCell];
        }
        // We can provide just the name of the renderer and GSGrid will try to do the rest END

        if (!column.align) column.align = "center"; // default alignment - center
        // Aligning the title of the column and the cell content
        if (!column.headerCellClass) column.headerCellClass = "";
        column.headerCellClass += ` ${getAlignmentClasses(column.align)}`;
        // Aligning the title of the column and the cell content END

        if (!column.cellClass) column.cellClass = column.renderCell ? "d-flex" : "d-initial";
        if (column.align) column.cellClass += ` ${getAlignmentClasses(column.align)}`;
        if (column.onCellClick) column.cellClass += " pointer justify-content-center";
        return column;
      });
    return columns.concat(resCols);
  }

  function alignRightFrozenCols(cols: Array<GSGridColumn>): Array<GSGridColumn> {
    const frozenLeftColumns = cols.filter((col: GSGridColumn) => col.frozen === "left");
    const normalColumns = cols.filter((col: GSGridColumn) => !col.frozen);
    let frozenRightColumns = cols.filter(
      (col: GSGridColumn) => col.frozen === "right" && !hiddenColumns.includes(col.key),
    );

    let frozenRight = 0;
    frozenRightColumns = frozenRightColumns
      //$FlowIgnore
      .toReversed()
      .map((col, idx) => {
        const cls = `d-flex frozen-right right-${frozenRight} ${
          idx + 1 == frozenRightColumns.length ? "first-col" : ""
        }`;
        col = {
          ...col,
          resizable: false,
          frozen: false, // if we delete the key it will mutate columns in props
          headerCellClass: `${col.headerCellClass} ${cls}`,
          cellClass: `${col.cellClass} ${cls}`,
        };
        frozenRight += col.width || col.minWidth || 50;
        return col;
      })
      .toReversed();
    return [...frozenLeftColumns, ...normalColumns, ...frozenRightColumns];
  }

  function reorderColumns(cols: Array<GSGridColumn>) {}

  function clearTimer() {
    if (intervalTimer) clearTimeout(intervalTimer);
    setIntervalTimer(_ => null);
  }

  function handleHideShowColumn(columnKey: string) {
    return function(value: boolean) {
      let hiddenCols = [...hiddenColumns];
      if (!hiddenCols.includes(columnKey)) {
        hiddenCols.push(columnKey);
      } else {
        hiddenCols = hiddenCols.filter(col => col != columnKey);
      }
      setHiddenColumns(_ => hiddenCols);
    };
  }

  function handleShowHeaderContextMenu(event: MouseEvent) {
    event.preventDefault();
    const target = findClickedCellEl(event);
    //$FlowIgnore
    if (target?.role == "columnheader") {
      setShowContextMenu(_ => {
        return {
          top: event.pageY,
          left: event.pageX,
          config: [
            ...propCols
              .filter(el => el.key !== "actionMenu")
              .map((col: GSGridColumn) => {
                return {
                  onClick: handleHideShowColumn(col.key),
                  label: (
                    <Checkbox
                      id={`${col.key}Grid`}
                      key={`${col.key}Grid`}
                      onChange={null}
                      checked={!hiddenColumns.includes(col.key)}
                      label={col.name}
                    />
                  ),
                };
              }),
          ],
        };
      });
    }
  }

  function handleOnCellContextMenu({ row, column, selectCell }, event: CellMouseEvent) {
    event.preventDefault();
    if (!selectedCells) {
      //$FlowIgnore
      const rowIndex = parseInt(event.target.parentNode?.ariaRowIndex || 0);
      // Incrementing by 1 because the original code returns indexes starting from 1 on the rows and actually this one is wrong
      const colIndex = parseInt(column.idx) + 1;
      setSelectedCells({
        start: { row: rowIndex, col: colIndex },
        end: { row: rowIndex, col: colIndex },
      });
    }
    setShowContextMenu(_ => {
      return {
        top: event.pageY,
        left: event.pageX,
        config: [
          {
            disabled: !selectedCells,
            onClick: handleCopyToClipboard,
            icon: "content_copy",
            label: props.t("COPY_SELECTION"),
          },
          {
            disabled: !selectedCells,
            onClick: handleDeselect,
            icon: "deselect",
            label: props.t("DESELECT"),
          },

          {
            icon: "select_all",
            label: props.t("SELECT_ALL"),
            className: !selectedCells ? "" : "disabled",
            onClick: handleSelectAll,
          },

          {
            disabled: !selectedCells,
            onClick: handleSelectColumn,
            icon: "text_rotate_vertical",
            label: props.t("SELECT_COLUMN"),
          },
          {
            disabled: !selectedCells,
            onClick: handleSelectRow,
            icon: "text_rotation_none",
            label: props.t("SELECT_ROW"),
          },
        ],
      };
    });
  }

  function handleHideContextMenu() {
    setShowContextMenu(_ => null);
  }

  function handleClick(event: MouseEvent) {
    if (showContextMenu) setShowContextMenu(_ => null);
  }

  function handleMouseDown(event: MouseEvent) {
    event.preventDefault();
    if (event.buttons != 1) return;
    const target = findClickedCellEl(event);
    // // Using my own functions and event listeners since the integrated
    // // doesn't support onMouseDown (only click)
    // // and we want to handle mouse down so we can drag the selection
    if (target) {
      const startCol = parseInt(target.getAttribute("aria-colindex"));
      const startRow = target.parentElement
        ? parseInt(target.parentElement.getAttribute("aria-rowindex"))
        : 0;

      setIntervalTimer(_ =>
        setTimeout(() => {
          setSelectedCells({
            start: { row: startRow, col: startCol },
            end: { row: startRow, col: startCol },
          });
        }, 250),
      );
      setSelectedCells(null);
    }
  }

  function handleMouseOver(event: MouseEvent) {
    const target = findClickedCellEl(event);
    if (!target) return;

    const role = target.getAttribute("role");

    if (role === "gridcell" || role === "columnheader") {
      const colIdx = parseInt(target.getAttribute("aria-colindex")) - 1;
      const currentCell = visibleColumns[parseInt(colIdx)];
      const tooltipObj =
        currentCell.hasOwnProperty("tooltip") || currentCell.hasOwnProperty("cellTooltip");

      if (colIdx != null && tooltipObj) {
        const tTip = role === "gridcell" ? currentCell.cellTooltip : currentCell.tooltip;

        if (!tTip) return;

        const { left, top } = target.getBoundingClientRect();
        const title =
          role === "gridcell"
            ? tTip.title || null
            : tTip.title || visibleColumns[parseInt(colIdx)].name;

        setTooltip({
          title,
          text: tTip.text,
          position: { x: left, y: top },
        });
      }
    }

    if (event.buttons != 1) return; // If not click and hold down

    const endCol = parseInt(target.getAttribute("aria-colindex"));
    const endRow = parseInt(
      (target.parentElement && target.parentElement.getAttribute("aria-rowindex")) || 0,
    );
    setSelectedCells({
      ...selectedCells,
      end: { row: endRow, col: endCol },
    });
  }
  function handleMouseOut(event: MouseEvent) {
    const target = findClickedCellEl(event);
    if (!target) return;

    if (target.getAttribute("role") === "columnheader") {
      setTooltip(null);
    }
  }

  function handleMouseUp(event: MouseEvent) {
    clearTimer();
  }

  function handleKeyDown(event: KeyboardEvent) {
    const target = event.target;
    const keyCode = event.keyCode; // the letter 'c' has keycode 67
    const keyC = 67; // the letter 'c' has keycode 67
    const isCtrlPressed = !!event.ctrlKey; // the letter 'c' has keycode 67

    // Deselect the cells and hide the contextMenu on pressing Esc
    if (keyCode === 27) {
      setSelectedCells(null);
      return;
    }
    // Copy the selection with Ctrl+C
    if (target && isCtrlPressed && keyCode === keyC) {
      handleCopyToClipboard();
      return;
    }
  }

  function findClickedCellEl(event: MouseEvent): Element | null {
    let cellEl = event.srcElement;
    while (cellEl) {
      if (cellEl.classList && cellEl.classList.contains("rdg-cell")) {
        return cellEl;
      } else cellEl = cellEl.parentElement;
    }
    return null;
  }

  function copyToClipboard() {
    const cellArray = getSelectedCellsArray();
    if (!cellArray.length) return;
    let row = null;
    // let prevRow = null;
    let col = null;
    let clipboardText = "";

    cellArray.forEach(cl => {
      if (cl.row !== row && row !== null) {
        clipboardText += "\n";
      }

      row = cl.row;
      col = cl.col;

      if (row === 1) {
        //Header is row 1
        clipboardText += `${visibleColumns[col - 1].name || ""}\t`;
      } else {
        //Cells
        if (row > 1 && enableVirtualization === true && visibleColumns) {
          clipboardText += `${rows[row - 2][visibleColumns[col - 1].key]}\t`;
        } else {
          const cell =
            gridRef.current &&
            gridRef.current.element.querySelector(
              `.gs-grid div[aria-rowindex="${row}"] div[aria-colindex="${col}"]`,
            );
          if (!!cell) {
            const cellCopy = cell.cloneNode(true);
            const icons = cellCopy.querySelectorAll(".material-icons");
            icons.forEach(icon => icon.remove());

            clipboardText += `${cellCopy.textContent.trim()}\t`; // might be changed with "innerText"  https://stackoverflow.com/questions/35213147/difference-between-textcontent-vs-innertext
          }
        }
      }
    });
    clipboardText = clipboardText.trim();
    if (window.isSecureContext && clipboardText) {
      navigator.clipboard.writeText(clipboardText);
    }
  }

  function getSelectedCellsArray(): { row: number, col: number }[] {
    if (!selectedCells || !selectedCells.start || !selectedCells.end) return [];
    const { start, end } = selectedCells;
    const seCells = [];

    let sRow = Math.min(start.row, end.row);
    let eRow = Math.max(start.row, end.row);
    let sCol = Math.min(start.col, end.col);
    let eCol = Math.max(start.col, end.col);

    if (!sRow && sRow !== 0 && eRow) sRow = eRow;
    if (!sCol && sCol !== 0 && eCol) sCol = eCol;

    for (let row = sRow; row <= eRow; row++) {
      for (let col = sCol; col <= eCol; col++) {
        seCells.push({ row, col });
      }
    }

    return seCells;
  }

  function removeSelectionClasses() {
    gridRef.current &&
      gridRef.current.element.querySelectorAll(".rdg-cell").forEach(el => {
        el && el.classList.remove("selection");
        const classes = el.className.split(" ").filter(className => {
          return !className.startsWith("selection-border-");
        });
        el.className = classes.join(" ");
      });
  }
  function onScroll() {
    highlightSelectedCells(getSelectedCellsArray());
  }

  function highlightSelectedCells(cells: { row: number, col: number }[]) {
    if (!selectedCells) return;
    if (!cells || cells.length < 1) return false;

    // Removing all classes related to this functionality
    removeSelectionClasses();
    cells.forEach(cl => {
      const cell =
        gridRef.current &&
        gridRef.current.element.querySelector(
          `.gs-grid div[aria-rowindex="${cl.row}"] div[aria-colindex="${cl.col}"]`,
        );

      const firstCol = Math.min(selectedCells.start.col, selectedCells.end.col);
      const lastCol = Math.max(selectedCells.start.col, selectedCells.end.col);
      const firstRow = Math.min(selectedCells.start.row, selectedCells.end.row);
      const lastRow = Math.max(selectedCells.start.row, selectedCells.end.row);

      if (firstCol === cl.col) {
        // Most left cells
        cell && cell.classList.add("selection-border-left");
        // Top most left
        if (firstRow === cl.row) cell && cell.classList.add("selection-border-top");
        // Bottom most left
        if (lastRow === cl.row) cell && cell.classList.add("selection-border-bottom");
      }

      if (lastCol === cl.col) {
        // most right
        cell && cell.classList.add("selection-border-right");
        // Top most left
        if (firstRow === cl.row) cell && cell.classList.add("selection-border-top");
        // Bottom most left
        if (lastRow === cl.row) cell && cell.classList.add("selection-border-bottom");
      }
      // Top and bottom rows
      if (firstRow === cl.row) cell && cell.classList.add("selection-border-top");
      if (lastRow === cl.row) cell && cell.classList.add("selection-border-bottom");
      // cell.dataset.value = cell.textContent;
      cell && cell.classList.add("selection");
    });
  }

  function getAlignmentClasses(align: GSGAlignmentTypes): string {
    const alignMap = { left: "start", right: "end", center: "center", middle: "center" };
    return `d-flex text-${alignMap[align] || align} align-items-center justify-content-${alignMap[
      align
    ] || align}`;
  }

  function measureColumnWidth(columnKey: string) {
    if (!gridRef.current.hasOwnProperty("element")) {
      return 0;
    }
    // $FlowIgnore
    return gridRef.current.element
      .querySelector(`[data-measuring-cell-key="${columnKey}"]`)
      ?.getBoundingClientRect().width;
  }

  function onColumnResize(idx: number, width: number): void {}

  function onSortColumnsChange(sortCols: Array<SortColumn>): void {
    const transformedSort: Object[] = sortCols.map((sc: Object): Object => {
      return {
        id: sc.columnKey.includes(".") ? sc.columnKey.split(".").pop() : sc.columnKey,
        direction: sc.direction,
      };
    });
    //$FlowIgnore
    typeof props.onSortChange === "function" && props.onSortChange(transformedSort[0] || []);
  }

  function sortColumns(props: Props): $ReadOnlyArray<SortColumn> {
    return props.sortColumns && props.sortColumns.length > 0
      ? props.sortColumns.map((col: SortColumn) => {
          return {
            //$FlowIgnore
            columnKey: col.columnKey || col.property || col.name,
            //$FlowIgnore
            direction: col.direction.toUpperCase(),
          };
        })
      : [];
  }

  function handleOnCellClick({ row, column, selectCell }, event: CellMouseEvent) {
    return column.onCellClick && column.onCellClick(row, column, event);
  }

  function handleCopyToClipboard() {
    copyToClipboard();
    setSelectedCells(null);
  }
  function handleDeselect() {
    setSelectedCells(null);
  }

  function handleSelectRow() {
    setSelectedCells({
      start: { col: 1, row: Math.min(selectedCells.start.row, selectedCells.end.row) },
      end: {
        col: visibleColumns.length,
        row: Math.max(selectedCells.start.row, selectedCells.end.row),
      },
    });
  }

  function handleSelectColumn() {
    setSelectedCells({
      start: { col: Math.min(selectedCells.start.col, selectedCells.end.col), row: 1 },
      end: {
        col: Math.max(selectedCells.start.col, selectedCells.end.col),
        row: rows.length + 1,
      },
    });
  }

  function handleSelectAll() {
    setSelectedCells({
      start: { col: 1, row: 1 },
      end: {
        col: visibleColumns.length,
        row: rows.length + 1,
      },
    });
  }

  function handleOnSelectedRowsChange(selRow) {
    if (props.hasOwnProperty("onSelectedRowsChange")) {
      setSelectedRows(selRow);
      // Converting the Set to Array so the components outside can work with the data (we don't use Set outside)
      return (
        props.hasOwnProperty("onSelectedRowsChange") &&
        props.onSelectedRowsChange(Array.from(selRow))
      );
    }
  }

  return (
    <View flex="1">
      <View className="d-print-none" flex={1} data-test-id={props.id}>
        {tooltip != null && (
          <GSTooltip text={tooltip.text} title={tooltip.title} position={tooltip.position} />
        )}
        {showContextMenu &&
          createPortal(
            <ContextMenu
              onHideContextMenu={handleHideContextMenu}
              config={showContextMenu.config}
              position={{
                top: showContextMenu.top,
                left: showContextMenu.left,
              }}
            />,
            document.body,
          )}
        {rows && rows.length > 0 && visibleColumns !== null ? (
          <DataGrid
            className={`gs-grid rdg-light d-print-none ${props.className || ""} ${
              rows && rows.length < 1 ? "opacity-50" : ""
            } ${intervalTimer !== null ? "cursor-cell" : ""}`}
            ref={gridRef}
            columns={visibleColumns}
            rows={rows}
            selectedRows={selectedRows}
            rowKeyGetter={props.rowKeyGetter || rowKeyGetter}
            rowClass={props.rowClass}
            onCellClick={handleOnCellClick}
            sortColumns={sortColumns(props)}
            onCellContextMenu={handleOnCellContextMenu}
            onSelectedRowsChange={handleOnSelectedRowsChange}
            onColumnResize={onColumnResize}
            onScroll={onScroll}
            onSortColumnsChange={onSortColumnsChange}
            defaultColumnOptions={{ minWidth: colMinWidth, maxWidth: colMaxWidth, resizable: true }}
            style={{
              // Setting it here since in the css I don't have access to the height and rows props
              blockSize: props.height || (rows.length ? "100%" : "200px"),
              maxHeight: props.maxHeight || "80vh",
            }}
            enableVirtualization={enableVirtualization}
            headerRowHeight={props.headerRowHeight || 40}
            rowHeight={props.rowHeight || 40}
          />
        ) : (
          <div className="border-1px-disabled-light07 text-color-disabled-light07 d-flex justify-content-center align-items-center h-200-px">
            <h5
              style={{
                textAlign: "center",
                gridColumn: "1/-1",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {props.noResultsText || "No results"}
            </h5>
          </div>
        )}
      </View>
      {/* Second (simple) version of the grid, just for printing */}
      <View className="d-print-only">
        <table border="1">
          <thead>
            <tr style={{ height: 40 }}>
              {visibleColumns.map(col => {
                return (
                  <th
                    style={{
                      textAlign: col.align || "left",
                      padding: "0 1rem",
                      lineHeight: "1rem",
                    }}
                    key={col.key}
                  >
                    {col.name}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map &&
              rows.map((row, idx) => {
                return (
                  <tr style={{ height: 40 }} key={idx}>
                    {visibleColumns.map(c => {
                      let cellContent = "";
                      if (c.key === "selectRow") {
                        cellContent = <Checkbox />;
                      } else if (c.renderCell) {
                        // $FlowIgnore
                        cellContent = c.renderCell({ row: row, column: c });
                      } else {
                        cellContent = row[c.key];
                      }

                      return (
                        <td
                          style={{
                            textAlign: c.align || "left",
                            padding: "0 1rem",
                            lineHeight: "1rem",
                          }}
                          key={c.key}
                        >
                          <div className="ellipsis">{cellContent}</div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </View>
    </View>
  );
}
GSGrid.render = GSRenderers;

export default localize()(GSGrid);
